"use client";

import Button from "@/components/Elements/Button";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Modal from "./Modal/Modal";
import ModalElement from "@/components/Modal/Modal";
import useServerTimeStore from "@/stores/useServerTimeStore";
import {
  useSocket,
  useSocketStatus,
} from "@/components/WebSocket/SocketProvider";

const connectionMessage = (
  <div>
    <p>
      För att du ska kunna följa budläggningen på ett rop så måste din
      webbläsare ha koppling till servern hela tiden. Om det inte finns så är
      det inte säkert att du ser den senaste informationen.
    </p>
    <p>
      Om du får en varning om att &quot;kopplingen till servern verkar ha
      förlorats&quot; så kan det bero på flera orsaker. Nedanstående åtgärder
      löser de flesta problem. Gör de i ordningen uppifrån och ner:
    </p>
    <ol>
      <li>
        <strong>Ladda om sidan</strong>
        &nbsp;- Detta löser de flesta problem
      </li>
      <li>
        <strong>Stäng alla utom en flik med tovek.se</strong> - Om det finns
        många flikar från tovek.se öppna kan alla anslutningar bli upptagna till
        din dator.
      </li>
      <li>
        <strong>Starta om webbläsaren</strong>&nbsp;- Stäng ner webbläsaren helt
        och hållet och starta den igen.
      </li>
      <li>
        <strong>Prova i en annan webbläsare</strong> - En vanlig webbläsare som
        fungerar på alla enheter är{" "}
        <a
          href="https://www.google.com/aclk?sa=l&amp;ai=DChcSEwiz2drksPTnAhWCtRgKHdG_ASsYABABGgJsZQ&amp;sig=AOD64_0QqaRfIiXRSeVtbWF-il3QMRSwMQ&amp;adurl=&amp;q=&amp;ved=2ahUKEwiY_tPksPTnAhXvwqYKHcd1AFAQqyQoAHoECBMQBw"
          target="_blank"
          rel="noopener"
        >
          Chrome
        </a>
      </li>
    </ol>
  </div>
);

const NetworkDetection = () => {
  const [isOnline, setOnline] = useState<boolean>(true);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [shouldMonitor, setShouldMonitor] = useState<boolean>(false);
  const { initialServerTime } = useServerTimeStore();

  const socket = useSocket();
  const { isConnected: socketConnected, connectionError } = useSocketStatus();

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const updateNetworkStatus = () => {
    setOnline(navigator.onLine);
  };

  // Start monitoring after initial delay to prevent flash on page load
  useEffect(() => {
    const delay = setTimeout(() => {
      setShouldMonitor(true);
    }, 8000); // Wait 8 seconds before starting to monitor

    return () => clearTimeout(delay);
  }, []);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const getErrorMessage = (isSocketIssue: boolean) => {
    if (isSocketIssue) {
      return "Kopplingen till servern verkar ha förlorats. (Felkod 400)";
    }
    return "Kopplingen till servern verkar ha förlorats.";
  };

  const showNetworkToast = (isSocketIssue: boolean = false) => {
    console.log("isSocketIssue parameter:", isSocketIssue);

    const errorMessage = getErrorMessage(isSocketIssue);

    console.log("Generated error message:", errorMessage);
    console.log("Should show Felkod 400:", isSocketIssue);

    toast(
      <div className="flex flex-col gap-4 text-center md:text-left md:flex-row items-center space-x-4 text-base md:text-lg">
        <span>{errorMessage}</span>

        <div className="div flex flex-row items-center space-x-2">
          <Button
            bgColor="white"
            textColor="black"
            borderColor="white"
            onClick={() => window.location.reload()}
          >
            Ladda om sidan
          </Button>
          <Button
            bgColor="transparent"
            textColor="white"
            borderColor="white"
            onClick={() => setModalOpen(true)}
          >
            Läs mer
          </Button>
        </div>
      </div>,
      {
        style: {
          background: "#333",
          color: "#fff",
          padding: "16px",
          bottom: "0px",
          border: "none",
          width: "100%",
        },
        id: "network-status-toast",
        duration: Infinity,
        position: "bottom-center",
      }
    );
  };

  // Monitor all network and socket conditions in one place
  useEffect(() => {
    // Don't monitor until after initial delay
    if (!shouldMonitor) {
      //console.log("Network monitoring not yet active, skipping check");
      return;
    }

    // console.log("=== NetworkDetection Status Check ===");
    // console.log("Socket connected:", socketConnected);
    // console.log("Connection error:", connectionError);
    // console.log("Browser online:", isOnline);
    // console.log("Initial server time:", !!initialServerTime);

    // Determine if we should show an error
    const hasSocketIssue = !socketConnected || !!connectionError;
    const hasNetworkIssue = !isOnline;
    const hasServerTimeIssue =
      !initialServerTime && socketConnected && isOnline;

    // console.log("Analysis:");
    // console.log("- Has socket issue:", hasSocketIssue);
    // console.log("- Has network issue:", hasNetworkIssue);
    // console.log("- Has server time issue:", hasServerTimeIssue);

    if(hasNetworkIssue) {
      console.log("Browser is offline, showing network toast");
      showNetworkToast(true);
      // Start reconnection interval
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          setOnline(navigator.onLine);
        }, 10000);
      }
      return;
    }

    // Show error if any issue exists
    if (hasSocketIssue || hasNetworkIssue || hasServerTimeIssue) {
      const isSocketIssue = hasSocketIssue;
      // console.log("Showing error toast, socket issue:", isSocketIssue);
      // TODO: Fix this toast display
      // showNetworkToast(isSocketIssue);
    } else {
      // console.log("All systems healthy - dismissing toast");
      toast.dismiss("network-status-toast");
    }

    // Clear reconnection interval when back online
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [
    shouldMonitor,
    socketConnected,
    connectionError,
    isOnline,
    initialServerTime,
    showNetworkToast,
  ]);

  // For server time
  useEffect(() => {
    window.addEventListener("load", updateNetworkStatus);
    window.addEventListener("online", updateNetworkStatus);
    window.addEventListener("offline", updateNetworkStatus);

    return () => {
      window.removeEventListener("load", updateNetworkStatus);
      window.removeEventListener("online", updateNetworkStatus);
      window.removeEventListener("offline", updateNetworkStatus);
    };
  }, []);

  return (
    <ModalElement
      isOpen={isModalOpen}
      onClose={() => setModalOpen(false)}
      modalTitle="Om kopplingen till servern"
      modalBody={connectionMessage}
      outerClassNames="!z-[99999]"
      size="lg"
    />
  );
};

export default NetworkDetection;
